import type { TrialBrandColors, TrialLeadOverrides, TrialResearchPreview } from '../types';

export const MAX_TRIAL_LOGO_BYTES = 2 * 1024 * 1024;
export const MAX_TRIAL_LOGO_SOURCE_BYTES = 8 * 1024 * 1024;
const TRIAL_LOGO_MAX_EDGE = 512;

export function isTrialLogoWithinLimit(file: File) {
  return file.size <= MAX_TRIAL_LOGO_BYTES;
}

export function isTrialLogoSourceWithinLimit(file: File) {
  return file.size <= MAX_TRIAL_LOGO_SOURCE_BYTES;
}

export function trialLogoScale(width: number, height: number) {
  return Math.min(1, TRIAL_LOGO_MAX_EDGE / Math.max(width, height, 1));
}

function rasterizeLogo(file: File, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(url);
      const scale = trialLogoScale(image.width, image.height);
      const canvas = document.createElement('canvas');

      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));

      const context = canvas.getContext('2d');

      if (!context) {
        resolve(null);
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), type, quality);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
}

export async function prepareTrialLogo(file: File): Promise<File | null> {
  if (!isTrialLogoSourceWithinLimit(file)) return null;
  if (isTrialLogoWithinLimit(file)) return file;

  const type = file.type === 'image/jpeg' || file.type === 'image/jpg' ? 'image/jpeg' : 'image/png';
  const blob = await rasterizeLogo(file, type, 0.86)
    ?? await rasterizeLogo(file, 'image/jpeg', 0.72);

  if (!blob || blob.size > MAX_TRIAL_LOGO_BYTES) return null;

  return new File([blob], file.name, { type: blob.type || type });
}

export const DEFAULT_TRIAL_COLOR = '#19214D';

export const DEFAULT_TRIAL_COLORS: TrialBrandColors = {
  main: DEFAULT_TRIAL_COLOR,
  button: DEFAULT_TRIAL_COLOR,
  link: DEFAULT_TRIAL_COLOR,
};

export const HEX_COLOR = /^#([0-9a-f]{6})$/i;

export function normalizeTrialAssetUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;

  return url.trim().replace(/&amp;/gi, '&');
}

export function isLightAssetLogo(url: string | null | undefined) {
  if (!url) return false;

  return /white|rgb-base-white|_light\.|logo-light/i.test(url);
}

export function trialLogoDisplayMode(
  preview: Pick<TrialResearchPreview, 'icon_url' | 'logo_url'>,
  values?: { logoUrl?: string | null },
): { variant: 'default' | 'onLight' } {
  if (values?.logoUrl) {
    return { variant: 'default' };
  }

  const logo = normalizeTrialAssetUrl(preview.logo_url);

  if (logo) {
    return { variant: 'default' };
  }

  return { variant: 'onLight' };
}

export function withTrialPreviewDefaults(preview: TrialResearchPreview): TrialResearchPreview {
  const locations = preview.locations ?? [];

  return {
    ...preview,
    name: preview.name?.trim() ?? '',
    logo_url: normalizeTrialAssetUrl(preview.logo_url),
    icon_url: normalizeTrialAssetUrl(preview.icon_url ?? null),
    colors: preview.colors ?? null,
    screenshot_url: normalizeTrialAssetUrl(preview.screenshot_url),
    images: (preview.images ?? [])
      .map((url) => normalizeTrialAssetUrl(url))
      .filter((url): url is string => !!url),
    industry_label: preview.industry_label ?? null,
    industry_proposed: preview.industry_proposed ?? null,
    confidence: preview.confidence ?? 0,
    locations,
    locations_total_found: preview.locations_total_found ?? locations.length,
    socials_found: preview.socials_found ?? [],
    sources_failed: preview.sources_failed ?? [],
    color_options: preview.color_options ?? [],
    mission_statement: preview.mission_statement ?? null,
    suggested_language_locale: preview.suggested_language_locale ?? null,
  };
}

export function colorsFromPreview(preview: TrialResearchPreview): TrialBrandColors {
  return preview.colors ?? DEFAULT_TRIAL_COLORS;
}

export function applyPrimaryColor(colors: TrialBrandColors, hex: string): TrialBrandColors {
  const next = hex.toLowerCase();

  return { ...colors, main: next, link: next };
}

export function applyAccentColor(colors: TrialBrandColors, hex: string): TrialBrandColors {
  return { ...colors, button: hex.toLowerCase() };
}

export function uniqueBrandColors(preview: TrialResearchPreview, selected?: TrialBrandColors | null): string[] {
  const values = [
    selected?.main,
    preview.colors?.main,
    preview.colors?.button,
    preview.colors?.link,
    ...(preview.color_options ?? []),
  ];

  return Array.from(new Set(
    values
      .filter((value): value is string => !!value && HEX_COLOR.test(value))
      .map((value) => value.toLowerCase()),
  ));
}

export function sameTrialColors(left: TrialBrandColors, right: TrialBrandColors) {
  return left.main.toLowerCase() === right.main.toLowerCase()
    && left.button.toLowerCase() === right.button.toLowerCase()
    && left.link.toLowerCase() === right.link.toLowerCase();
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function resolveTrialLogoSources(
  preview: Pick<TrialResearchPreview, 'icon_url' | 'logo_url'>,
  values: { logoUrl?: string | null },
): { iconUrl: string | null; logoUrl: string | null } {
  if (values.logoUrl) {
    return { iconUrl: values.logoUrl, logoUrl: values.logoUrl };
  }

  return {
    iconUrl: normalizeTrialAssetUrl(preview.icon_url),
    logoUrl: normalizeTrialAssetUrl(preview.logo_url),
  };
}

export async function trialBrandingPayload(
  values: {
    colors: TrialBrandColors;
    logoFile?: File | null;
    logoUrl?: string | null;
  },
  fromSite: TrialBrandColors,
  preview: Pick<TrialResearchPreview, 'icon_url' | 'logo_url'>,
): Promise<Pick<TrialLeadOverrides, 'theme' | 'logo_data' | 'logo_url'>> {
  const logoFile = values.logoFile && isTrialLogoWithinLimit(values.logoFile)
    ? values.logoFile
    : null;
  const theme = !sameTrialColors(values.colors, fromSite) ? {
    theme: {
      main_color: values.colors.main,
      button_color: values.colors.button,
      link_color: values.colors.link,
    },
  } : {};

  if (logoFile) {
    return {
      ...theme,
      logo_data: await fileToDataUrl(logoFile),
    };
  }

  const researchLogo = normalizeTrialAssetUrl(preview.logo_url)
    ?? normalizeTrialAssetUrl(preview.icon_url);

  if (researchLogo && !values.logoUrl) {
    return {
      ...theme,
      logo_url: researchLogo,
    };
  }

  return theme;
}
